import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CommunityScoreChart from '../components/CommunityScoreChart';
import CommunityYouTubePlayer from '../components/CommunityYouTubePlayer';
import Seo from '../components/Seo';
import { useI18n } from '../i18n/I18nContext';
import getApiBase from '../utils/apiBase';
import { useAuth } from './AuthContext';
import { communityAverage, parseYouTubeVideoId, scoreSummary } from './communityScoringUtils';
import './CommunityScoring.css';

const API_BASE = getApiBase();
const REPORT_REASONS = ['spam', 'harassment', 'inappropriate', 'other'];

const readResponse = async (response, fallback) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(fallback);
  return data;
};

const promptForReportReason = (t) => {
  const labels = REPORT_REASONS.map((reason) => t(`community.report.${reason}`));
  const answer = window.prompt(t('community.report.prompt', { reasons: labels.join(', ') }), labels[0]);
  if (answer === null) return null;
  const normalized = answer.trim().toLocaleLowerCase();
  return REPORT_REASONS.find((reason, index) => (
    normalized === reason || normalized === labels[index].toLocaleLowerCase()
  )) || 'other';
};

const CommunityScoringLanding = ({ error, loading, onChooseVideo, onSubmitUrl, setUrl, url, videos }) => {
  const { t } = useI18n();
  return <>
    <section className="community-entry-hero">
      <div className="community-entry-copy">
        <span className="community-eyebrow">{t('community.entry.eyebrow')}</span>
        <h1>{t('community.entry.title')}</h1>
        <p>{t('community.entry.description')}</p>
        <form onSubmit={onSubmitUrl} className="community-entry-form">
          <label htmlFor="entry-youtube-url">{t('community.entry.start')}</label>
          <div>
            <input id="entry-youtube-url" value={url} onChange={(event) => setUrl(event.target.value)} placeholder={t('community.entry.pasteUrl')} required />
            <button type="submit">{t('community.entry.enter')}</button>
          </div>
        </form>
        {error && <p className="community-message error" role="alert">{error}</p>}
        <a className="community-learn-link" href="#how-it-works">{t('community.entry.learn')} <span aria-hidden="true">↓</span></a>
      </div>
      <div className="community-entry-visual" aria-hidden="true">
        <div className="community-entry-score"><small>{t('community.entry.liveScore')}</small><strong>+07</strong></div>
        <svg viewBox="0 0 620 270" preserveAspectRatio="none">
          <path className="entry-grid" d="M0 54H620M0 108H620M0 162H620M0 216H620" />
          <path className="entry-score-line" d="M0 190H85V160H150V195H235V125H320V150H390V82H485V108H545V45H620" />
        </svg>
        <div className="community-entry-keys">
          <div className="entry-key minus"><kbd>F</kbd><span>−1</span><small>{t('community.leftHand')}</small></div>
          <div className="entry-key plus"><kbd>J</kbd><span>+1</span><small>{t('community.rightHand')}</small></div>
        </div>
      </div>
    </section>

    <section className="community-how" id="how-it-works">
      <div className="community-section-heading"><span>{t('community.how.kicker')}</span><h2>{t('community.how.title')}</h2></div>
      <div className="community-how-grid">
        <article><strong>01</strong><h3>{t('community.how.chooseTitle')}</h3><p>{t('community.how.chooseDescription')}</p></article>
        <article><strong>02</strong><h3>{t('community.how.scoreTitle')}</h3><p>{t('community.how.scoreDescription')}</p></article>
        <article><strong>03</strong><h3>{t('community.how.publishTitle')}</h3><p>{t('community.how.publishDescription')}</p></article>
      </div>
    </section>

    <section className="community-recent">
      <div className="community-section-heading"><span>{t('community.recent.kicker')}</span><h2>{t('community.recent.title')}</h2></div>
      {loading ? <p className="community-entry-status">{t('community.recent.loading')}</p> : videos.length ? (
        <div className="community-recent-grid">
          {videos.slice(0, 6).map((item) => (
            <button type="button" key={item.video_id} onClick={() => onChooseVideo(item.video_id)}>
              <span className="community-recent-image"><img src={item.thumbnail_url} alt="" loading="lazy" /><i>{t('community.recent.enter')}</i></span>
              <strong>{item.title}</strong>
              <small>{t('community.recent.judges', { count: item.score_count })}</small>
            </button>
          ))}
        </div>
      ) : (
        <div className="community-entry-empty"><strong>{t('community.recent.emptyTitle')}</strong><p>{t('community.recent.emptyDescription')}</p></div>
      )}
    </section>
  </>;
};

const CommunityVideoSidebar = ({ activeVideoId, loadMore, nextCursor, onChooseVideo, onSubmitUrl, open, setUrl, toggle, url, videos }) => {
  const { t } = useI18n();
  return <>
    <button
      type="button"
      className={`community-sidebar-toggle${open ? ' open' : ''}`}
      aria-controls="community-video-sidebar"
      aria-expanded={open}
      aria-label={open ? t('community.sidebar.hide') : t('community.sidebar.show')}
      onClick={toggle}
    >
      <span aria-hidden="true">{open ? '‹' : '›'}</span>
    </button>
    <aside id="community-video-sidebar" className={`community-video-sidebar${open ? ' open' : ''}`} aria-hidden={!open} inert={open ? undefined : ''}>
      <h2>{t('community.sidebar.title')}</h2>
      <form onSubmit={onSubmitUrl} className="community-url-form compact">
        <label htmlFor="sidebar-youtube-url">{t('community.sidebar.add')}</label>
        <div><input id="sidebar-youtube-url" value={url} onChange={(event) => setUrl(event.target.value)} placeholder={t('community.sidebar.paste')} required /><button type="submit">{t('community.sidebar.open')}</button></div>
      </form>
      <div className="community-video-list">
        {videos.map((item) => (
          <button type="button" key={item.video_id} className={item.video_id === activeVideoId ? 'active' : ''} onClick={() => onChooseVideo(item.video_id)}>
            <img src={item.thumbnail_url} alt="" loading="lazy" />
            <span><strong>{item.title}</strong><small>{t('community.sidebar.scores', { count: item.score_count })}</small></span>
          </button>
        ))}
        {nextCursor && <button type="button" className="load-more-videos" onClick={loadMore}>{t('community.sidebar.more')}</button>}
      </div>
    </aside>
  </>;
};

const ReplyList = ({ replies, scoreId, user, refresh, runWrite }) => {
  const { t, formatDate } = useI18n();
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const submitReply = async (event) => {
    event.preventDefault();
    await runWrite(`/api/community/scores/${scoreId}/replies`, {
      method: 'POST',
      body: JSON.stringify({ content: replyText, parent_id: replyingTo?.id || null }),
    });
    setReplyText('');
    setReplyingTo(null);
    await refresh();
  };

  const editReply = async (reply) => {
    const content = window.prompt(t('community.reply.editPrompt'), reply.content);
    if (content === null || content.trim() === reply.content) return;
    await runWrite(`/api/community/replies/${reply.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
    await refresh();
  };

  const deleteReply = async (reply) => {
    if (!window.confirm(t('community.reply.deleteConfirm'))) return;
    await runWrite(`/api/community/replies/${reply.id}`, { method: 'DELETE' });
    await refresh();
  };

  const reportReply = async (reply) => {
    const reason = promptForReportReason(t);
    if (reason === null) return;
    await runWrite('/api/community/reports', {
      method: 'POST',
      body: JSON.stringify({ target_type: 'reply', target_id: reply.id, reason }),
    });
    window.alert(t('community.report.submitted'));
  };

  return (
    <div className="community-replies">
      {replies.map((reply) => (
        <article key={reply.id} className={`community-reply depth-${reply.depth}`}>
          <header>
            <strong>{reply.author_name}</strong>
            <time dateTime={reply.created_at}>{formatDate(reply.created_at, { dateStyle: 'medium', timeStyle: 'short' })}</time>
          </header>
          {reply.placeholder ? <p className="community-placeholder">{reply.placeholder}</p> : (
            <p>{reply.reply_to_name ? <span className="reply-target">@{reply.reply_to_name} </span> : null}{reply.content}</p>
          )}
          <div className="community-inline-actions">
            {user && reply.status === 'visible' && <button type="button" onClick={() => { setReplyingTo(reply); setReplyText(''); }}>{t('community.actions.reply')}</button>}
            {reply.is_owner && reply.status === 'visible' && <button type="button" onClick={() => editReply(reply)}>{t('community.actions.edit')}</button>}
            {reply.is_owner && reply.status !== 'deleted' && <button type="button" onClick={() => deleteReply(reply)}>{t('community.actions.delete')}</button>}
            {user && !reply.is_owner && reply.status === 'visible' && <button type="button" onClick={() => reportReply(reply)}>{t('community.actions.report')}</button>}
          </div>
        </article>
      ))}
      {user && (
        <form className="community-reply-form" onSubmit={submitReply}>
          <label htmlFor={`reply-${scoreId}`}>{replyingTo ? t('community.reply.to', { name: replyingTo.author_name }) : t('community.reply.join')}</label>
          <textarea id={`reply-${scoreId}`} value={replyText} onChange={(event) => setReplyText(event.target.value)} maxLength="1000" rows="2" required />
          <div>
            {replyingTo && <button type="button" className="secondary" onClick={() => setReplyingTo(null)}>{t('community.actions.cancel')}</button>}
            <button type="submit">{t('community.reply.post')}</button>
          </div>
        </form>
      )}
    </div>
  );
};

const ScorePost = ({ score, user, refresh, runWrite }) => {
  const { t, formatDate } = useI18n();
  const editScore = async () => {
    const comment = window.prompt(t('community.score.editPrompt'), score.comment);
    if (comment === null || comment.trim() === score.comment) return;
    await runWrite(`/api/community/scores/${score.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ comment }),
    });
    await refresh();
  };

  const deleteScore = async () => {
    if (!window.confirm(t('community.score.deleteConfirm'))) return;
    await runWrite(`/api/community/scores/${score.id}`, { method: 'DELETE' });
    await refresh();
  };

  const reportScore = async () => {
    const reason = promptForReportReason(t);
    if (reason === null) return;
    await runWrite('/api/community/reports', {
      method: 'POST',
      body: JSON.stringify({ target_type: 'score', target_id: score.id, reason }),
    });
    window.alert(t('community.report.submitted'));
  };

  return (
    <article className="community-score-post">
      <header className="community-score-post-header">
        <div><strong>{score.author_name}</strong><time dateTime={score.created_at}>{formatDate(score.created_at, { dateStyle: 'medium', timeStyle: 'short' })}</time></div>
        {score.status === 'visible' && <div className="published-total"><strong>{score.total}</strong><span>+{score.plus_count} / −{score.minus_count}</span></div>}
      </header>
      {score.placeholder ? <p className="community-placeholder score-placeholder">{score.placeholder}</p> : (
        <>
          <CommunityScoreChart events={score.events} duration={score.duration_seconds} compact />
          {score.comment && <p className="community-score-comment">{score.comment}</p>}
        </>
      )}
      <div className="community-inline-actions">
        {score.is_owner && score.status === 'visible' && <button type="button" onClick={editScore}>{t('community.actions.editComment')}</button>}
        {score.is_owner && score.status !== 'deleted' && <button type="button" onClick={deleteScore}>{t('community.actions.delete')}</button>}
        {user && !score.is_owner && score.status === 'visible' && <button type="button" onClick={reportScore}>{t('community.actions.report')}</button>}
      </div>
      <ReplyList replies={score.replies || []} scoreId={score.id} user={user} refresh={refresh} runWrite={runWrite} />
    </article>
  );
};

const CommunityScoring = () => {
  const { videoId: routeVideoId } = useParams();
  const { t, formatNumber } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const playerRef = useRef(null);
  const workspaceRef = useRef(null);
  const translationRef = useRef(t);
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [url, setUrl] = useState('');
  const [video, setVideo] = useState(null);
  const [scores, setScores] = useState([]);
  const [events, setEvents] = useState([]);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth > 760);

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  const summary = useMemo(() => scoreSummary(events), [events]);
  const average = useMemo(() => communityAverage(scores), [scores]);

  const loadVideos = useCallback(async (cursor = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/community/videos?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
      const data = await readResponse(response, translationRef.current('community.error.videos'));
      setVideos((current) => cursor ? [...current, ...data.items] : data.items);
      setNextCursor(data.next_cursor || null);
      return data.items;
    } catch {
      setError(translationRef.current('community.error.videos'));
      return [];
    } finally {
      if (!cursor) setLoadingVideos(false);
    }
  }, []);

  const loadVideo = useCallback(async (resetDraft = true) => {
    if (!routeVideoId) return;
    try {
      const response = await fetch(`${API_BASE}/api/community/videos/${encodeURIComponent(routeVideoId)}`, { credentials: 'include' });
      const data = await readResponse(response, translationRef.current('community.error.discussion'));
      setVideo(data.video);
      setScores(data.scores || []);
      if (resetDraft) {
        setEvents([]);
        setDuration(0);
        setPlaying(false);
        setComment('');
      }
      setError('');
    } catch {
      setError(translationRef.current('community.error.discussion'));
    }
  }, [routeVideoId]);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  useEffect(() => { loadVideo(true); }, [loadVideo]);

  useEffect(() => {
    const warnBeforeUnload = (event) => {
      if (!events.length) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [events.length]);

  const runWrite = useCallback(async (path, options) => {
    setError('');
    const response = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
    return readResponse(response, translationRef.current('community.error.request'));
  }, []);

  const chooseVideo = (id) => {
    if (id === routeVideoId) return;
    if (events.length && !window.confirm(t('community.room.discard'))) return;
    navigate(`/community/scoring/${id}`);
    if (window.innerWidth <= 760) setSidebarOpen(false);
  };

  const submitUrl = (event) => {
    event.preventDefault();
    const id = parseYouTubeVideoId(url);
    if (!id) {
      setError(t('community.error.url'));
      return;
    }
    chooseVideo(id);
  };

  const recordScore = useCallback((delta) => {
    if (!playing || !playerRef.current?.isPlaying()) return;
    const time = Math.round(playerRef.current.getCurrentTime() * 1000) / 1000;
    setDuration((current) => Math.max(current, time + 1));
    setEvents((current) => [...current, { time, delta }]);
  }, [playing]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName) || event.target.isContentEditable) return;
      const key = event.key.toLowerCase();
      if (key !== 'f' && key !== 'j') return;
      event.preventDefault();
      recordScore(key === 'j' ? 1 : -1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [recordScore]);

  const publish = async (event) => {
    event.preventDefault();
    if (!user || !routeVideoId || !events.length || !duration) return;
    setPublishing(true);
    setStatus('');
    try {
      await runWrite(`/api/community/videos/${routeVideoId}/my-score`, {
        method: 'PUT',
        body: JSON.stringify({ events, duration_seconds: duration, comment }),
      });
      setEvents([]);
      setComment('');
      setStatus('community.room.published');
      await Promise.all([loadVideo(false), loadVideos()]);
    } catch {
      setError(t('community.error.request'));
    } finally {
      setPublishing(false);
    }
  };

  const focusScoringWorkspace = useCallback(() => workspaceRef.current?.focus({ preventScroll: true }), []);
  const handlePlayerReady = useCallback((value) => setDuration(value), []);
  const handlePlayingChange = useCallback((value) => {
    setPlaying(value);
    if (value) focusScoringWorkspace();
  }, [focusScoringWorkspace]);
  const handlePlayerError = useCallback((message) => setError(message), []);

  const refreshDiscussion = useCallback(async () => {
    try {
      await loadVideo(false);
    } catch {
      setError(translationRef.current('community.error.discussion'));
    }
  }, [loadVideo]);

  const safeWrite = useCallback(async (path, options) => {
    try {
      return await runWrite(path, options);
    } catch (writeError) {
      setError(translationRef.current('community.error.request'));
      throw writeError;
    }
  }, [runWrite]);

  const canonicalUrl = routeVideoId ? `/community/scoring/${routeVideoId}` : '/community/scoring';
  const videoSidebar = (
    <CommunityVideoSidebar
      activeVideoId={routeVideoId}
      loadMore={() => loadVideos(nextCursor)}
      nextCursor={nextCursor}
      onChooseVideo={chooseVideo}
      onSubmitUrl={submitUrl}
      open={sidebarOpen}
      setUrl={setUrl}
      toggle={() => setSidebarOpen((open) => !open)}
      url={url}
      videos={videos}
    />
  );

  if (!routeVideoId) {
    return (
      <main className={`community-scoring-page community-entry-page${sidebarOpen ? ' community-sidebar-visible' : ' community-sidebar-hidden'}`}>
        <Seo title={t('community.seo.title')} description={t('community.seo.landingDescription')} path={canonicalUrl} />
        {videoSidebar}
        <CommunityScoringLanding error={error} loading={loadingVideos} onChooseVideo={chooseVideo} onSubmitUrl={submitUrl} setUrl={setUrl} url={url} videos={videos} />
      </main>
    );
  }

  return (
    <main className={`community-scoring-page${sidebarOpen ? ' community-sidebar-visible' : ' community-sidebar-hidden'}`}>
      <Seo title={t('community.seo.title')} description={t('community.seo.roomDescription')} path={canonicalUrl} />
      {videoSidebar}

      <div className="community-main-column">
        <section className="community-intro">
          <span>{t('community.entry.eyebrow')}</span>
          <h1>{t('community.room.title')}</h1>
          <p>{t('community.room.description')}</p>
        </section>

        {error && <p className="community-message error" role="alert">{error}</p>}
        {status && <p className="community-message success" role="status">{t(status)}</p>}

        <section className="community-workspace" ref={workspaceRef} tabIndex="-1">
              <div className="community-video-panel">
                <div className="community-video-frame">
                  <CommunityYouTubePlayer key={routeVideoId} ref={playerRef} videoId={routeVideoId} onReady={handlePlayerReady} onPlayingChange={handlePlayingChange} onError={handlePlayerError} onRequestFocus={focusScoringWorkspace} />
                </div>
                <div className="mobile-score-controls">
                   <button type="button" className="minus" onClick={() => recordScore(-1)} disabled={!playing}>−1 <span>{t('community.leftHand')}</span></button>
                   <button type="button" className="plus" onClick={() => recordScore(1)} disabled={!playing}>+1 <span>{t('community.rightHand')}</span></button>
                </div>
              </div>
              <div className="community-live-panel">
                 <div className="community-live-total"><span>{t('community.room.live')}</span><strong>{summary.total}</strong><small>+{summary.plus} / −{summary.minus}</small></div>
                <div className="community-average-score">
                   <span>{t('community.room.average')}</span>
                   {average.count ? <strong>{t('community.room.averageJudges', { average: formatNumber(average.average, { minimumFractionDigits: 1, maximumFractionDigits: 1 }), count: average.count })}</strong> : <strong className="empty">{t('community.room.noAverage')}</strong>}
                </div>
                 <div className="community-key-guide"><span><kbd>F</kbd> {t('community.leftHand')} −1</span><span><kbd>J</kbd> {t('community.rightHand')} +1</span></div>
                 {!playing && <p className="community-play-hint">{t('community.room.playHint')}</p>}
                <CommunityScoreChart events={events} duration={duration} />
                <div className="community-score-actions">
                   <button type="button" onClick={() => setEvents((current) => current.slice(0, -1))} disabled={!events.length}>{t('community.room.undo')}</button>
                   <button type="button" onClick={() => setEvents([])} disabled={!events.length}>{t('community.room.reset')}</button>
                </div>
              </div>
        </section>

        <section className="community-discussion">
              <div className="community-discussion-heading"><div><span>{t('community.room.nowScoring')}</span><h2>{video?.title || t('community.room.videoFallback', { videoId: routeVideoId })}</h2></div><strong>{t('community.room.communityScores', { count: scores.filter((score) => score.status === 'visible').length })}</strong></div>
              {user ? (
                <form className="community-publish-form" onSubmit={publish}>
                  <label htmlFor="score-comment">{t('community.room.notes')} <span>{t('community.room.optional')}</span></label>
                  <textarea id="score-comment" value={comment} onChange={(event) => setComment(event.target.value)} maxLength="1000" rows="3" placeholder={t('community.room.notesPlaceholder')} />
                  <button type="submit" disabled={!events.length || !duration || publishing}>{publishing ? t('community.room.publishing') : t('community.room.publish')}</button>
                </form>
              ) : (
                <p className="community-login-prompt"><Link to={`/login?next=${encodeURIComponent(canonicalUrl)}`}>{t('community.room.login')}</Link>{t('community.room.loginBetween')}<Link to={`/register?next=${encodeURIComponent(canonicalUrl)}`}>{t('community.room.register')}</Link>{t('community.room.loginAfter')}</p>
              )}
              <div className="community-score-posts">
                 {!scores.length && <p className="community-no-scores">{t('community.room.noScores')}</p>}
                {scores.map((score) => <ScorePost key={score.id} score={score} user={user} refresh={refreshDiscussion} runWrite={safeWrite} />)}
              </div>
        </section>
      </div>
    </main>
  );
};

export default CommunityScoring;
